/**
 * useRoom.ts
 * Subscribes to the active room's real-time RTDB data
 * and keeps the app store in sync.
 */

import { useEffect } from 'react';
import { subscribeToRoom } from '@db/roomService';
import { useAppStore } from '@store/appStore';

export function useRoom(roomCode: string | null): void {
  const setActiveRoom = useAppStore((s) => s.setActiveRoom);
  const setCurrentSlideConfig = useAppStore((s) => s.setCurrentSlideConfig);

  useEffect(() => {
    if (!roomCode) return;

    const unsubscribe = subscribeToRoom(roomCode, (room) => {
      // Guard against a resurrection race: after the teacher ends the session
      // we set activeRoom = null, but this listener can fire once more before
      // React commits the unsubscribe and would write the still-active room
      // straight back. Ignore any late callback once the store no longer holds
      // this room (ended, or switched to a different room).
      const current = useAppStore.getState().activeRoom;
      if (!current || current.roomCode !== roomCode) return;

      setActiveRoom(room);
      setCurrentSlideConfig(room?.activeConfig ?? null);
    });

    return unsubscribe;
  }, [roomCode, setActiveRoom, setCurrentSlideConfig]);
}
